import {DocumentNode, useApolloClient} from "@apollo/client";
import {useState} from "react";
import {toast} from "react-hot-toast";

import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import {DropdownAction, DropdownActionItem, DropdownItem} from "@/components/quickActionsBar/DropdownAction.tsx";
import {QuickActionsBar} from "@/components/quickActionsBar/QuickActionsBar.tsx";
import {GQL_STATUS_PAGE_QUERY} from "@/lib/queries/statuspages.ts";
import {
  GQL_EXTERNAL_USERS,
  GQL_EXTERNAL_USER_RESET_PASSWORD,
  GQL_EXTERNAL_USER_TOGGLE,
  UpdateExternalUserResponse,
} from "@/lib/queries/statuspagesExternalUsers.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {ExternalUser} from "@/lib/types/statuspages.ts";
import {deleteExternalUser} from "@/pages/statuspages/utils/quickActions.ts";

interface ExternalUserActionsProps {
  statusPageID: number;
  user: ExternalUser;
  onSuccess: () => void;
}

export const ExternalUserRowActions = ({statusPageID, user, onSuccess}: ExternalUserActionsProps) => {
  const [show, setShow] = useState(false);
  const client = useApolloClient();
  const externalUserAction = async (statusPageID: number, userId: number, mutation: DocumentNode) => {
    const {data} = await client.mutate<UpdateExternalUserResponse>({
      mutation: mutation,
      variables: {statusPageID, userId},
      refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_EXTERNAL_USERS],
    });

    return data?.response;
  };

  const actionItems: DropdownItem[] = [];

  if (user.is_active) {
    actionItems.push(
      <DropdownActionItem
        text="Deactivate"
        onClick={async () => {
          await externalUserAction(statusPageID, user.id, GQL_EXTERNAL_USER_TOGGLE);
        }}
      />
    );
  } else {
    actionItems.push(
      <DropdownActionItem
        text="Reactivate"
        onClick={async () => {
          await externalUserAction(statusPageID, user.id, GQL_EXTERNAL_USER_TOGGLE);
        }}
      />
    );
  }

  actionItems.push(
    <DropdownActionItem
      text="Reset Password"
      onClick={async () => {
        const resp = await externalUserAction(statusPageID, user.id, GQL_EXTERNAL_USER_RESET_PASSWORD);
        if (resp?.status === ResponseStatus.SUCCESS) {
          toast.success(
            <span>
              An email with password reset instructions has been sent to: <strong>{user.email}</strong>
            </span>
          );
        }
      }}
    />
  );
  actionItems.push(<DropdownActionItem text={"Delete"} onClick={() => setShow(true)} />);
  return (
    <>
      <QuickActionsBar
        id={`external-user-actions-${user.id}`}
        actions={[<DropdownAction id={`external-user-actions-${user.id}`} items={actionItems} />]}
      />
      <DeleteActionDialog
        show={show}
        title="Delete External User"
        onConfirm={async () => {
          await deleteExternalUser(statusPageID, user.id);
          setShow(false);
          onSuccess();
        }}
        onCancel={() => {
          setShow(false);
        }}
      >
        Are you sure you want to delete External User?
      </DeleteActionDialog>
    </>
  );
};
